import { withEventScope } from '@repo/event-processor';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';

import useSubscriptionPreview from '../../hooks/useSubscriptionPreview';
import { PlanPurchaseDetailsProps } from '../PlanPurchaseDetails/types';

import PaymentStep from './PaymentStep';
import { CheckoutFlowVariants } from './types';
import VariantA from './VariantA';
import VariantB from './VariantB';

type VariantFlowProps = PlanPurchaseDetailsProps & {
  /**
   * The selected subscription plan
   */
  plan: SubscriptionPlanType;
  /**
   * The variant to show
   */
  variant: string;
  /**
   * If true, show Variant A, since there is only one plan available
   */
  shouldShowOnlyOnePlan?: boolean;
};

/**
 * VariantFlow determines which variant (A or B) to show during the checkout flow.
 * It also handles the payment step.
 */
const VariantFlow: React.FC<VariantFlowProps> = ({
  onPaymentInit,
  subscriptionFlowState,
  returnUrlParams,
  onPaymentSuccess,
  plan,
  variant,
  shouldShowOnlyOnePlan,
}) => {
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const subscriptionChangePreviewData = useSubscriptionPreview({
    currentSubscription,
  });

  if (subscriptionFlowState === 'selection') {
    /*
    Show variant A flow for these conditions -
      1) based on feature flag
      2) when only EXPERT plan is available to be upgrade to, in otherwords, the current plan is PRO
      3) when checkout modal is opened from pricing page plans
    */

    if (
      variant === CheckoutFlowVariants.VariantA ||
      plan === SubscriptionPlanType.EXPERT ||
      shouldShowOnlyOnePlan
    ) {
      return <VariantA onPaymentInit={onPaymentInit} />;
    } else {
      return <VariantB onPaymentInit={onPaymentInit} />;
    }
  }

  return (
    <PaymentStep
      returnUrlParams={returnUrlParams}
      subscriptionChangePreviewData={subscriptionChangePreviewData}
      onPaymentSuccess={onPaymentSuccess}
    />
  );
};

export default withEventScope(VariantFlow, { module: 'variantFlow' });
